import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useMemo } from "react";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { AccountCard } from "@components/financialAccount/AccountCard/AccountCard";
import { EmptyValue } from "@design-system/EmptyValue/EmptyValue";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { FinancialAccount } from "@common/types/financialAccount";
import { InvoiceSource } from "@common/types/invoice";
import { KickAccountCard } from "@components/invoices/views/KickAccountCard";
import { MatchableItem, MatchItemType } from "../types";

export const FinancialAccountCell = memo(
    ({ row }: CellContext<ViewTableFeatures, MatchableItem, unknown>) => {
        const { financialAccountId, invoiceSource, type } = row.original;
        const accounts = useFinancialAccounts();
        const account = useMemo<FinancialAccount | null>(() => {
            if (financialAccountId == null) {
                return null;
            }
            return (
                accounts.find(
                    (a: FinancialAccount) => a.id === financialAccountId,
                ) ?? null
            );
        }, [accounts, financialAccountId]);

        const isKickNativeInvoice =
            (type === MatchItemType.INVOICE ||
                type === MatchItemType.LINE_ITEM) &&
            invoiceSource === InvoiceSource.MANUAL;
        if (isKickNativeInvoice) {
            return (
                <ViewTableCell>
                    <KickAccountCard iconSize="sm" />
                </ViewTableCell>
            );
        }

        if (!account) {
            return (
                <ViewTableCell>
                    <EmptyValue />
                </ViewTableCell>
            );
        }

        return (
            <ViewTableCell>
                <AccountCard
                    account={account}
                    onlyNumber
                    displayNameAsTooltip
                    showType={false}
                    iconSize="sm"
                />
            </ViewTableCell>
        );
    },
);
FinancialAccountCell.displayName = "FinancialAccountCell";
